import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Clock, RefreshCw, LogOut, Sparkles } from 'lucide-react';

export default function PendingApprovalPage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [checking, setChecking] = useState(false);

  const handleCheck = async () => {
    setChecking(true);
    const updated = await refreshUser();
    setChecking(false);
    if (updated?.is_verified) {
      toast.success('You\'re approved! Welcome to BeautyPro.');
    } else {
      toast.info('Still pending. We\'ll email you as soon as you\'re approved.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center px-4" data-testid="pending-approval-page">
      <Card className="w-full max-w-md border-stone-200">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#D97757] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading text-xl font-semibold text-stone-900">BeautyPro</span>
          </div>
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-2">
            <Clock className="w-7 h-7 text-amber-600" />
          </div>
          <CardTitle className="font-heading text-2xl text-stone-900">Pending approval</CardTitle>
          <CardDescription className="text-stone-600">
            Thanks for signing up{user?.full_name ? `, ${user.full_name}` : ''}! Our team is reviewing
            your professional license and ID. You'll get an email at{' '}
            <span className="font-medium text-stone-800">{user?.email}</span> once you're approved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={handleCheck}
            disabled={checking}
            className="w-full bg-[#D97757] hover:bg-[#C06040] text-white"
            data-testid="check-approval-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Checking...' : 'Check status'}
          </Button>
          <Button variant="outline" onClick={handleLogout} className="w-full" data-testid="pending-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
          <p className="text-xs text-center text-stone-500">
            Questions? Contact info@beautyproreview.com
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
